import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '@env/environment';

// Interface pour les messages WebSocket
export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private socket: WebSocket | null = null;
  private readonly wsUrl = environment.production 
    ? 'ws://localhost:3000/socket.io/' 
    : 'ws://localhost:3000/socket.io/';

  // Observables pour les différents types de messages
  private messagesSubject = new BehaviorSubject<WebSocketMessage | null>(null);
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private resourceUpdatesSubject = new BehaviorSubject<any>(null);

  public messages$ = this.messagesSubject.asObservable();
  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  public resourceUpdates$ = this.resourceUpdatesSubject.asObservable();

  constructor() {
    if (!environment.production) {
      console.log('🔌 WebSocket Service initialized');
    }
  }

  /**
   * Se connecter au WebSocket via Load Balancer
   */
  connect(): Observable<boolean> {
    if (!environment.production) {
      console.log('🔌 WebSocket désactivé temporairement (backend sans Socket.IO)');
    }
    
    // Simuler connexion réussie sans vraiment se connecter
    this.connectionStatusSubject.next(false);
    return this.connectionStatus$;

    // Code original désactivé temporairement
    /*
    if (this.socket?.readyState === WebSocket.OPEN) {
      return this.connectionStatus$;
    }

    try {
      this.socket = new WebSocket(this.wsUrl);
      
      this.socket.onopen = (event) => {
        if (!environment.production) {
          console.log('✅ WebSocket connecté:', this.wsUrl);
        }
        this.connectionStatusSubject.next(true);
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          
          if (!environment.production) {
            console.log('📨 WebSocket message reçu:', message);
          }

          // Traiter les différents types de messages
          this.handleMessage(message);
          
        } catch (error) {
          console.error('❌ Erreur parsing WebSocket message:', error);
        }
      };

      this.socket.onclose = (event) => {
        if (!environment.production) {
          console.log('🔌 WebSocket fermé:', event.code, event.reason);
        }
        this.connectionStatusSubject.next(false);
        
        // Reconnexion automatique après 5 secondes
        setTimeout(() => {
          if (!environment.production) {
            console.log('🔄 Tentative reconnexion WebSocket...');
          }
          this.connect();
        }, 5000);
      };

      this.socket.onerror = (error) => {
        console.error('❌ Erreur WebSocket:', error);
        this.connectionStatusSubject.next(false);
      };

    } catch (error) {
      console.error('❌ Erreur connexion WebSocket:', error);
      this.connectionStatusSubject.next(false);
    }

    return this.connectionStatus$;
    */
  }

  /**
   * Traiter les messages entrants
   */
  private handleMessage(message: WebSocketMessage): void {
    // Diffuser le message générique
    this.messagesSubject.next(message);

    // Traiter selon le type de message
    switch (message.type) {
      case 'resourceUpdate':
      case 'resourceUpdated':
        this.resourceUpdatesSubject.next(message.data);
        if (!environment.production) {
          console.log('🔄 Resource update:', message.data);
        }
        break;
        
      case 'notification':
        // Traiter notifications système
        if (!environment.production) {
          console.log('📢 Notification:', message.data);
        }
        break;
        
      case 'userPresence':
        // Traiter présence utilisateurs
        if (!environment.production) {
          console.log('👤 User presence:', message.data);
        }
        break;
        
      default:
        if (!environment.production) {
          console.log('📨 Message type inconnu:', message.type);
        }
    }
  }

  /**
   * Envoyer un message
   */
  send(type: string, data: any): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      const message: WebSocketMessage = {
        type,
        data,
        timestamp: new Date()
      };
      
      this.socket.send(JSON.stringify(message));
      
      if (!environment.production) {
        console.log('📤 Message envoyé:', message);
      }
    } else {
      console.warn('⚠️ WebSocket non connecté, impossible d\'envoyer:', type, data);
    }
  }

  /**
   * Se déconnecter
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.connectionStatusSubject.next(false);
      
      if (!environment.production) {
        console.log('🔌 WebSocket déconnecté manuellement');
      }
    }
  }

  /**
   * Vérifier si connecté
   */
  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /**
   * Obtenir les statistiques de connexion
   */
  getConnectionInfo(): any {
    return {
      url: this.wsUrl,
      connected: this.isConnected(),
      readyState: this.socket?.readyState,
      protocol: this.socket?.protocol
    };
  }
}